import type { Nullable } from '@shared/types/utils';

import { HOTEL_PRICES } from '@shared/utils-frontend/navigation-service';

export type NavElements = Array<{
  id: string;
  element: Nullable<HTMLElement>;
}>;

export type ContainerOffset = {
  offsetHeight: number;
  offsetTop: number;
};

export type NavigationStateType = {
  getNeedFixed: getNeedFixedType;
  getSelected: getSelectedType;
  getShowFilters: getShowFiltersType;
};

type getNeedFixedType = (scrollY: number) => boolean;
type getSelectedType = (scrollY: number) => Nullable<string>;
type getShowFiltersType = (
  scrollY: number,
  extraOffsetTop?: number,
  extraOffsetBottom?: number,
) => boolean;

type Props = {
  navElements: NavElements;
  containerOffset: ContainerOffset;
};

type NavigationStateFunctionType = ({
  containerOffset,
  navElements,
}: Props) => NavigationStateType;

const NavigationState: NavigationStateFunctionType = ({
  containerOffset,
  navElements,
}) => {
  const getSelectedElement = (scrollY: number) => {
    const { offsetHeight: navigationHeight } = containerOffset;
    const selectedElements = navElements.filter(
      (item) =>
        item.element && scrollY >= item.element.offsetTop - navigationHeight,
    );
    if (!selectedElements.length) {
      return null;
    }

    return selectedElements[selectedElements.length - 1];
  };

  const getNeedFixed: getNeedFixedType = (scrollY) => {
    if (containerOffset && containerOffset.offsetTop) {
      return scrollY >= containerOffset.offsetTop;
    }
    return false;
  };

  const getSelected: getSelectedType = (scrollY) => {
    const selectedElement = getSelectedElement(scrollY);
    if (!selectedElement) {
      return null;
    }

    const { id } = selectedElement;

    return id;
  };

  const getShowFilters: getShowFiltersType = (
    scrollY,
    extraOffsetTop = -56,
    extraOffsetBottom = 64,
  ) => {
    const priceEle = navElements.find(({ id }) => id === HOTEL_PRICES);
    if (!priceEle || !priceEle.element) {
      return false;
    }
    const { offsetHeight, offsetTop } = priceEle.element;

    return (
      scrollY > offsetTop + extraOffsetTop &&
      scrollY < offsetHeight + offsetTop - extraOffsetBottom
    );
  };

  return {
    getNeedFixed,
    getSelected,
    getShowFilters,
  };
};

export default NavigationState;
